import Header from "../../components/Header";
import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import {
    Alert, Avatar, Box, Button, Card, Chip, Skeleton, Table, TableBody, TableCell, TableContainer, TableHead,
    TableRow, Typography,
} from '@mui/material';
import PersonAddAlt1Icon from '@mui/icons-material/PersonAddAlt1';
import AdminPanelSettingsOutlinedIcon from '@mui/icons-material/AdminPanelSettingsOutlined';
import { MascaraTelefone } from "../../../utils/MascaraTelefone";
import { FormatDate } from "../../../utils/FormatDate";
import AdicionarAdmin from "./AdicionarAdmin";
import { CabecalhoUsuarios, CampoBusca, campo, corPrimaria, filtrarUsuarios, iniciais } from "./Comum";

// E-mail do admin logado (payload do JWT), para marcar "você" na lista
const emailLogado = () => {
    try {
        const payload = (localStorage.getItem('token') || "").split(".")[1].replace(/-/g, "+").replace(/_/g, "/");
        return String(JSON.parse(atob(payload)).email || "").toLowerCase();
    } catch {
        return "";
    }
};

function Admins() {

    const token = localStorage.getItem('token');
    const [usuarios, setUsuarios] = useState([]);
    const [carregando, setCarregando] = useState(true);
    const [erro, setErro] = useState("");
    const [busca, setBusca] = useState("");
    const [adicionarAdmin, setAdicionarAdmin] = useState(false);
    const meuEmail = emailLogado();

    useEffect(() => {
        const getUsuarios = async () => {
            try {
                const response = await axios.get(`${process.env.REACT_APP_API_URL}/listarAdmins`, {
                    headers: {
                        Authorization: token
                    }
                })
                const lista = response.data.usuarios || [];
                setUsuarios([...lista].sort((a, b) => String(campo(a, "nome")).localeCompare(String(campo(b, "nome")), "pt-BR")));
                if (response.data.status !== 200 && response.data.status !== 400) setErro(response.data.erro || "Não foi possível carregar os administradores.");
            } catch (err) {
                console.error(err);
                setErro("Não foi possível carregar os administradores. Tente novamente mais tarde.");
            } finally {
                setCarregando(false);
            }
        }

        getUsuarios();
    }, [token, adicionarAdmin])

    const adminsFiltrados = useMemo(() => filtrarUsuarios(usuarios, busca), [usuarios, busca]);


    return (
        <div>
            <Header />
            <Box sx={{ width: "100%", display: "flex", justifyContent: "center", pb: 6 }}>
                <Box sx={{ width: "90%", maxWidth: 1280, mt: 2.5 }}>
                    <CabecalhoUsuarios
                        titulo="Usuários"
                        descricao={`Quem tem acesso a este painel administrativo${carregando ? "" : ` · ${usuarios.length} no total`}.`}
                        acoes={
                            <Button
                                variant="contained"
                                size="small"
                                startIcon={<PersonAddAlt1Icon />}
                                sx={{ backgroundColor: corPrimaria, textTransform: "none", "&:hover": { backgroundColor: "#153d66" } }}
                                onClick={() => setAdicionarAdmin(true)}
                            >
                                Adicionar Admin
                            </Button>
                        }
                    />

                    <Card variant="outlined" sx={{ borderRadius: 2 }}>
                        <Box sx={{ p: 2.5 }}>
                            <CampoBusca valor={busca} onChange={setBusca} placeholder="Buscar por nome, e-mail ou telefone" />
                        </Box>

                        {erro && <Alert severity="error" sx={{ mx: 2.5, mb: 2 }}>{erro}</Alert>}

                        <TableContainer sx={{ borderTop: 1, borderColor: "divider" }}>
                            <Table size="small" sx={{ minWidth: 640 }}>
                                <TableHead>
                                    <TableRow sx={{ backgroundColor: "#fafafa", "& th": { fontWeight: 600, color: "text.secondary", fontSize: 13, py: 1.5 } }}>
                                        <TableCell>Administrador</TableCell>
                                        <TableCell>Telefone</TableCell>
                                        <TableCell>Nível</TableCell>
                                        <TableCell>Cadastro</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {carregando ? (
                                        Array.from({ length: 3 }).map((_, i) => (
                                            <TableRow key={i}>
                                                {Array.from({ length: 4 }).map((__, j) => <TableCell key={j} sx={{ py: 2 }}><Skeleton /></TableCell>)}
                                            </TableRow>
                                        ))
                                    ) : adminsFiltrados.length === 0 ? (
                                        <TableRow>
                                            <TableCell colSpan={4} sx={{ py: 6, textAlign: "center", borderBottom: 0 }}>
                                                <AdminPanelSettingsOutlinedIcon sx={{ fontSize: 40, color: "text.disabled" }} />
                                                <Typography sx={{ fontWeight: 500, mt: 1 }}>Nenhum administrador encontrado</Typography>
                                                {busca && <Typography variant="body2" color="text.secondary">Tente outro termo de busca.</Typography>}
                                            </TableCell>
                                        </TableRow>
                                    ) : (
                                        adminsFiltrados.map((usuario) => {
                                            const nome = campo(usuario, "nome");
                                            const email = campo(usuario, "email");
                                            const telefone = campo(usuario, "telefone");
                                            const nivel = usuario?.admin?.Nivel ?? usuario?.admin?.nivel;
                                            const cadastro = usuario?.createdAt || usuario?.created_at;
                                            const souEu = meuEmail && String(email).toLowerCase() === meuEmail;
                                            return (
                                                <TableRow key={usuario?.idUsuario} hover sx={{ "& td": { py: 1.5 } }}>
                                                    <TableCell>
                                                        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                                                            <Avatar sx={{ width: 36, height: 36, fontSize: 14, backgroundColor: "#ffc845", color: corPrimaria, fontWeight: 700 }}>
                                                                {iniciais(nome)}
                                                            </Avatar>
                                                            <Box sx={{ minWidth: 0 }}>
                                                                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                                                                    <Typography variant="body2" sx={{ fontWeight: 500 }}>{nome || "Sem nome"}</Typography>
                                                                    {souEu && <Chip label="Você" size="small" sx={{ height: 18, fontSize: 10 }} />}
                                                                </Box>
                                                                <Typography variant="caption" color="text.secondary">{email}</Typography>
                                                            </Box>
                                                        </Box>
                                                    </TableCell>
                                                    <TableCell sx={{ whiteSpace: "nowrap" }}>{telefone ? MascaraTelefone(telefone) : "—"}</TableCell>
                                                    <TableCell>
                                                        {nivel !== undefined && nivel !== null
                                                            ? <Chip size="small" variant="outlined" label={`Nível ${nivel}`} />
                                                            : "—"}
                                                    </TableCell>
                                                    <TableCell sx={{ whiteSpace: "nowrap" }}>{cadastro ? FormatDate(cadastro) : "—"}</TableCell>
                                                </TableRow>
                                            );
                                        })
                                    )}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    </Card>
                </Box>
            </Box>
            <AdicionarAdmin adicionarAdmin={adicionarAdmin} setAdicionarAdmin={setAdicionarAdmin} />
        </div>
    );
}

export default Admins;
